import { request } from '@/lib/api'

/**
 * Автосценарии (тикет #116, сервер — #115): каталог, правила, журнал запусков, проверка на записи.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено». Журнал приходит строками
 * таблицы, поэтому ключи в нём snake_case (`ran_at`, `entity_id`), а не camelCase.
 */

export type AutomationFieldType = 'text' | 'number' | 'boolean' | 'uuid' | 'date' | 'datetime'

export interface AutomationField {
  name: string
  type: AutomationFieldType
  label: string
  /** Сообщает ли о нём «поле изменилось»: вычисляемые факты не «меняются». */
  trackable: boolean
  /** Операторы, которые сервер примет для этого поля. */
  operators?: string[]
}

export interface AutomationEntity {
  code: string
  label: string
  triggers?: string[]
  fields?: AutomationField[]
  anchors?: { name: string; label: string }[]
  /** Что значит «нарушен срок» у этой сущности; нет — своего срока нет. */
  sla?: string | null
  actions?: string[]
}

export interface AutomationActionParam {
  name: string
  kind: 'text' | 'longtext' | 'hours' | 'employee' | 'choice' | 'recipient' | 'template' | 'url' | string
  required: boolean
  options?: string[]
}

export interface AutomationCatalog {
  entities?: AutomationEntity[]
  operators?: { code: string; sign: string }[]
  actions?: string[]
  actionParams?: Record<string, AutomationActionParam[]>
  recipients?: string[]
  maxDepth?: number
}

export interface AutomationRule {
  id: string
  version: number
  name: string
  description?: string | null
  entityType: string
  trigger: string
  conditions?: unknown
  actions?: unknown
  timer?: unknown
  executionOrder: number
  active: boolean
  armedAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface AutomationRuleBody {
  name: string
  description: string | null
  entityType: string
  trigger: string
  conditions: unknown
  actions: unknown[]
  timer: unknown
  active: boolean
  executionOrder?: number
  version?: number
}

export interface AutomationRun {
  id: number | string
  ran_at: string | number
  entity_type: string
  entity_id: string
  entity_label?: string | null
  trigger_event: string
  matched: boolean
  actions_run?: number | null
  error?: string | null
  reason?: string | null
  depth?: number | null
}

export interface AutomationActionRun {
  id: number | string
  action_key: string
  entity_type: string
  entity_id: string
  entity_label?: string | null
  action_type: string
  outcome: string
  detail?: string | null
  ran_at: string | number
}

export interface AutomationExplanation {
  kind: 'all' | 'any' | 'not' | 'leaf'
  condition: string
  matched: boolean
  field?: string | null
  actual?: unknown
  reason?: string | null
  items?: AutomationExplanation[]
}

export interface AutomationEvaluation {
  matched: boolean
  reason?: string | null
  explanation?: AutomationExplanation
  facts?: Record<string, unknown>
}

const BASE = '/admin/automation'

export function fetchAutomationCatalog(signal?: AbortSignal): Promise<AutomationCatalog> {
  return request<AutomationCatalog>(`${BASE}/catalog`, { signal })
}

export function fetchAutomationRules(signal?: AbortSignal): Promise<AutomationRule[]> {
  return request<{ rules?: AutomationRule[] }>(`${BASE}/rules`, { signal }).then((body) => body.rules ?? [])
}

export function createAutomationRule(body: AutomationRuleBody): Promise<AutomationRule> {
  return request<AutomationRule>(`${BASE}/rules`, { method: 'POST', body })
}

export function updateAutomationRule(id: string, body: AutomationRuleBody): Promise<AutomationRule> {
  return request<AutomationRule>(`${BASE}/rules/${id}`, { method: 'PUT', body })
}

export function setAutomationRuleActive(id: string, active: boolean): Promise<AutomationRule> {
  return request<AutomationRule>(`${BASE}/rules/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST', body: {} })
}

/** Порядок внутри одной сущности и одного триггера; правила не взводятся заново. */
export function reorderAutomationRules(ids: string[]): Promise<AutomationRule[]> {
  return request<{ rules?: AutomationRule[] }>(`${BASE}/rules/order`, { method: 'PUT', body: { ids } })
    .then((body) => body.rules ?? [])
}

export function fetchAutomationRuns(id: string, entityId: string | null, signal?: AbortSignal):
  Promise<{ runs: AutomationRun[]; actions: AutomationActionRun[] }> {
  const query = entityId ? `?entityId=${encodeURIComponent(entityId)}` : ''
  return request<{ runs?: AutomationRun[]; actions?: AutomationActionRun[] }>(`${BASE}/rules/${id}/runs${query}`, { signal })
    .then((body) => ({ runs: body.runs ?? [], actions: body.actions ?? [] }))
}

export function evaluateAutomationRule(id: string, entityId: string): Promise<AutomationEvaluation> {
  return request<AutomationEvaluation>(`${BASE}/rules/${id}/evaluate`, { method: 'POST', body: { entityId } })
}
